<script lang="ts" setup>
import { computed, ref } from 'vue'
import { NColorPicker, useMessage } from 'naive-ui'
import type { Language, Theme } from '@/store/modules/app/helper'
import { SvgIcon } from '@/components/common'
import { useAppStore, useUserStore } from '@/store'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import { t } from '@/locales'
import { useIframeTheme } from '@/composables/useIframeTheme'

const { sendThemeToIframes } = useIframeTheme()

const appStore = useAppStore()
const userStore = useUserStore()

const { isMobile } = useBasicLayout()

const ms = useMessage()

const theme = computed(() => appStore.theme)

const userInfo = computed(() => userStore.userInfo)

const avatar = ref(userInfo.value.avatar ?? '')

const name = ref(userInfo.value.name ?? '')

const description = ref(userInfo.value.description ?? '')

const language = computed({
  get() {
    return appStore.language
  },
  set(value: Language) {
    appStore.setLanguage(value)
  },
})

const colorTheme = computed({
  get() {
    return appStore.colorTheme
  },
  set(value: string) {
    appStore.setColorTheme(value)
  },
})

const themeOptions: { label: string; key: Theme; icon: string }[] = [
  {
    label: 'Auto',
    key: 'auto',
    icon: 'ri:contrast-line',
  },
  {
    label: 'Light',
    key: 'light',
    icon: 'ri:sun-foggy-line',
  },
  {
    label: 'Dark',
    key: 'dark',
    icon: 'ri:moon-foggy-line',
  },
]

const languageOptions: { label: string; key: Language; value: Language }[] = [
  { label: 'English', key: 'en-US', value: 'en-US' },
  { label: 'Tiếng Việt', key: 'vi-VN', value: 'vi-VN' },

]

function updateUserInfo(options: Partial<any>) {
  userStore.updateUserInfo(options)
  ms.success(t('common.success'))
}

function handleThemeChange(themeKey: any) {
  appStore.setTheme(themeKey)
  sendThemeToIframes(themeKey)
}
</script>

<template>
  <div class="p-4 space-y-5 min-h-[200px]">
    <div class="space-y-6">
      <!--      <div class="flex items-center space-x-4"> -->
      <!--        <span class="flex-shrink-0 w-[100px]">{{ $t('setting.avatarLink') }}</span> -->
      <!--        <div class="flex-1"> -->
      <!--          <NInput v-model:value="avatar" placeholder="" /> -->
      <!--        </div> -->
      <!--        <NButton size="tiny" text type="primary" @click="updateUserInfo({ avatar })"> -->
      <!--          {{ $t('common.save') }} -->
      <!--        </NButton> -->
      <!--      </div> -->
      <div class="flex items-center space-x-4">
        <span class="flex-shrink-0 w-[100px]">{{ $t('setting.name') }}</span>
        <div class="w-[200px]">
          <AppInput v-model:value="name" placeholder="" />
        </div>
        <AppButton size="tiny" text type="primary" @click="updateUserInfo({ name })">
          <SvgIcon icon="ion:save-outline" />
        </AppButton>
      </div>
      <div class="flex items-center space-x-4">
        <span class="flex-shrink-0 w-[100px]">{{ $t('setting.description') }}</span>
        <div class="flex-1">
          <AppInput v-model:value="description" placeholder="" />
        </div>
        <AppButton size="tiny" text type="primary" @click="updateUserInfo({ description })">
          <SvgIcon icon="ion:save-outline" />
        </AppButton>
      </div>
      <div class="flex items-center space-x-4">
        <span class="flex-shrink-0 w-[100px]">{{ $t('setting.theme') }}</span>
        <div class="flex flex-wrap items-center gap-4">
          <template v-for="item of themeOptions" :key="item.key">
            <AppButton
              size="small"
              :type="item.key === theme ? 'primary' : undefined"
              @click="handleThemeChange(item.key)"
            >
              <template #icon>
                <SvgIcon :icon="item.icon" />
              </template>
            </AppButton>
          </template>
        </div>
      </div>
      <div class="flex items-center space-x-4">
        <span class="flex-shrink-0 w-[100px]">{{ $t('setting.language') }}</span>
        <div class="flex flex-wrap items-center gap-4">
          <AppSelect
            style="width: 140px"
            :value="language"
            :options="languageOptions"
            :clearable="false"
            @update-value="value => appStore.setLanguage(value)"
          />
        </div>
      </div>

      <div class="flex items-center space-x-4">
        <span class="flex-shrink-0 w-[100px]">{{ $t('setting.colorTheme') }}</span>
        <NColorPicker :modes="['hex']" :default-value="colorTheme" class="custom-color-picker" @update-value="value => appStore.setColorTheme(value)" />
      </div>
      <!--      <div class="flex items-center space-x-4"> -->
      <!--        <span class="flex-shrink-0 w-[100px]">{{ $t('setting.resetUserInfo') }}</span> -->
      <!--        <NButton size="small" @click="handleReset"> -->
      <!--          {{ $t('common.reset') }} -->
      <!--        </NButton> -->
      <!--      </div> -->
    </div>
  </div>
</template>

<style>
.custom-color-picker .n-color-picker-trigger .n-color-picker-trigger__fill {
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	overflow: hidden;
}

.custom-color-picker .n-color-picker-trigger .n-color-picker-trigger__fill :not(.n-color-picker-checkboard, .n-color-picker-trigger__value) {
	border-radius: 12px;
}
</style>
